import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Layers, Plus, Trash2, RefreshCw, Copy, Check, Sparkles, Cpu } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import CodeBlock from "./CodeBlock";

// Default node templates per engine. Picked to be tiny + obviously demoable.
const TEMPLATES = {
  SpriteKit: [
    { kind: "SKSpriteNode", color: "yellow", size: [64, 64], position: [375, 667], name: "Player", physics: "dynamic" },
    { kind: "SKSpriteNode", color: "brown", size: [750, 32], position: [375, 64], name: "Ground", physics: "static" },
    { kind: "SKLabelNode", text: "Score 0", fontSize: 32, fontColor: "black", position: [375, 1200], name: "score" },
    { kind: "SKShapeNode", shape: "circle", size: [24], fillColor: "saffron", position: [200, 700], name: "Coin" },
  ],
  SceneKit: [
    { kind: "SCNSphere", radius: 0.5, position: [0, 1, 0], color: "orange", name: "Player" },
    { kind: "SCNBox", size: [0.5, 0.5, 0.5], position: [1, 0.5, 0], color: "blue", name: "Block" },
    { kind: "SCNFloor", name: "floor" },
    { kind: "SCNLight", type: "omni", position: [0, 8, 4] },
    { kind: "SCNCamera", position: [0, 4, 8] },
  ],
  RealityKit: [
    { kind: "ModelEntity.sphere", radius: 0.06, position: [0, 0.06, 0], color: "orange", name: "Player" },
    { kind: "ModelEntity.box", size: [0.1, 0.1, 0.1], position: [0.15, 0.05, 0], color: "blue", name: "Block" },
    { kind: "ModelEntity.cylinder", height: 0.005, radius: 0.04, position: [0.2, 0.005, -0.1], color: "yellow", name: "Coin" },
  ],
};

const ENGINES = ["SpriteKit", "SceneKit", "RealityKit"];

/** Convert any node into the editor's working shape (defaults applied). */
function normalizeNode(n, idx) {
  return {
    id: n.id || (n.name ? n.name.toLowerCase() : `node${idx + 1}`),
    name: n.name || `node${idx + 1}`,
    ...n,
  };
}

export default function SceneEditorDialog({ open, onOpenChange, blueprint, onApply }) {
  const initialEngine = blueprint?.engine || "SpriteKit";
  const [engine, setEngine] = useState(initialEngine);
  const [nodes, setNodes] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [code, setCode] = useState("");
  const [filename, setFilename] = useState("GameScene.swift");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const e = blueprint?.engine || "SpriteKit";
    setEngine(e);
    // Seed from blueprint.scene_tree if present, otherwise use templates
    const seed = (blueprint?.scene_tree?.[0]?.nodes || [])
      .slice(0, 5)
      .map((n, i) => ({ kind: TEMPLATES[e][0].kind, ...TEMPLATES[e][i % TEMPLATES[e].length], name: n }))
      .map(normalizeNode);
    setNodes(seed.length ? seed : TEMPLATES[e].slice(0, 3).map(normalizeNode));
    setSelectedIdx(0);
    setCode("");
  }, [open, blueprint?.engine, blueprint?.scene_tree]);

  // When the engine flips, reset to that engine's templates
  const switchEngine = (e) => {
    setEngine(e);
    setNodes(TEMPLATES[e].slice(0, 3).map(normalizeNode));
    setSelectedIdx(0);
    setCode("");
  };

  const selected = nodes[selectedIdx];
  const availableKinds = useMemo(() => {
    const all = TEMPLATES[engine].map((t) => t.kind);
    return [...new Set(all)];
  }, [engine]);

  const addNode = (kind) => {
    const tpl = TEMPLATES[engine].find((t) => t.kind === kind) || TEMPLATES[engine][0];
    const next = [...nodes, normalizeNode({ ...tpl, name: `${tpl.kind}_${nodes.length + 1}` }, nodes.length)];
    setNodes(next);
    setSelectedIdx(next.length - 1);
  };

  const removeNode = (idx) => {
    const next = nodes.filter((_, i) => i !== idx);
    setNodes(next);
    setSelectedIdx(Math.min(selectedIdx, Math.max(0, next.length - 1)));
  };

  const updateField = (key, value) => {
    const next = [...nodes];
    next[selectedIdx] = { ...next[selectedIdx], [key]: value };
    setNodes(next);
  };

  const compile = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/games/scene/compile", {
        scene: { engine, nodes },
      });
      setCode(data.code);
      setFilename(data.filename);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Compile failed");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    if (!code) return;
    await navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Swift copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const apply = () => {
    if (!code) return toast.error("Compile first");
    onApply?.({ engine, scene: { engine, nodes }, code, filename });
    toast.success("Applied to your blueprint");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream border-2 border-ink rounded-3xl max-w-5xl max-h-[92vh] overflow-y-auto p-0" data-testid="scene-editor-dialog">
        <div className="px-6 pt-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
              <Layers className="w-5 h-5 text-marigold" /> Visual Scene Editor
            </DialogTitle>
            <DialogDescription className="text-ink2">
              Drag, drop, tweak — get clean Swift back. Switch engines to see the same idea in SpriteKit, SceneKit, or RealityKit.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-full border-2 border-ink p-1 text-xs font-bold uppercase tracking-wider">
              {ENGINES.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => switchEngine(e)}
                  className={`px-3 py-1.5 rounded-full transition ${engine === e ? "bg-ink text-cream" : "text-ink2 hover:text-ink"}`}
                  data-testid={`scene-engine-${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
            <span className="text-xs text-ink2 inline-flex items-center gap-1">
              <Cpu className="w-3 h-3" /> {nodes.length} nodes
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-6 py-4">
          {/* Left: Node list */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">Nodes</span>
              <select
                onChange={(e) => { if (e.target.value) { addNode(e.target.value); e.target.value = ""; } }}
                className="text-xs px-2 py-1 rounded-full border border-border bg-paper outline-none cursor-pointer"
                data-testid="scene-add-select"
              >
                <option value="">+ Add</option>
                {availableKinds.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <ul className="space-y-1" data-testid="scene-node-list">
              {nodes.map((n, i) => (
                <li key={i}>
                  <button
                    onClick={() => setSelectedIdx(i)}
                    data-testid={`scene-node-${i}`}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition flex items-center gap-2 ${
                      selectedIdx === i ? "border-marigold bg-marigold/5" : "border-border hover:border-ink"
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full bg-marigold flex-shrink-0" />
                    <span className="font-mono text-xs truncate flex-1">{n.name || n.id}</span>
                    <span className="text-[10px] text-ink2 truncate">{n.kind}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); removeNode(i); }}
                      className="text-ink2 hover:text-red-500"
                      data-testid={`scene-remove-${i}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </button>
                </li>
              ))}
              {nodes.length === 0 && (
                <li className="text-xs text-ink2 italic px-3 py-2">No nodes yet — add one above.</li>
              )}
            </ul>
          </div>

          {/* Middle: Property editor */}
          <div className="space-y-3">
            <span className="label-eyebrow">Properties</span>
            {selected ? (
              <div className="card-soft p-3 space-y-2 text-xs" data-testid="scene-props">
                <Field label="name" value={selected.name || ""} onChange={(v) => updateField("name", v)} testid="prop-name" />
                {"color" in selected && <Field label="color" value={selected.color || ""} onChange={(v) => updateField("color", v)} hint="yellow / orange / blue / red / teal / brown / black / white" testid="prop-color" />}
                {"text" in selected && <Field label="text" value={selected.text || ""} onChange={(v) => updateField("text", v)} testid="prop-text" />}
                {"fontSize" in selected && <Field label="fontSize" type="number" value={selected.fontSize ?? ""} onChange={(v) => updateField("fontSize", Number(v) || 0)} testid="prop-fontSize" />}
                {"fontColor" in selected && <Field label="fontColor" value={selected.fontColor || ""} onChange={(v) => updateField("fontColor", v)} testid="prop-fontColor" />}
                {"radius" in selected && <Field label="radius" type="number" step="0.01" value={selected.radius ?? ""} onChange={(v) => updateField("radius", Number(v) || 0)} testid="prop-radius" />}
                {"height" in selected && <Field label="height" type="number" step="0.01" value={selected.height ?? ""} onChange={(v) => updateField("height", Number(v) || 0)} testid="prop-height" />}
                {"size" in selected && (
                  <Field
                    label="size"
                    value={(selected.size || []).join(", ")}
                    onChange={(v) => updateField("size", v.split(",").map((x) => Number(x.trim()) || 0))}
                    hint="comma-sep — w, h or w, h, d"
                    testid="prop-size"
                  />
                )}
                {"position" in selected && (
                  <Field
                    label="position"
                    value={(selected.position || []).join(", ")}
                    onChange={(v) => updateField("position", v.split(",").map((x) => Number(x.trim()) || 0))}
                    hint="comma-sep — x, y or x, y, z"
                    testid="prop-position"
                  />
                )}
                {"physics" in selected && (
                  <Field
                    label="physics"
                    type="select"
                    value={selected.physics || ""}
                    onChange={(v) => updateField("physics", v)}
                    options={["", "dynamic", "static"]}
                    testid="prop-physics"
                  />
                )}
                {"shape" in selected && (
                  <Field label="shape" type="select" value={selected.shape || "circle"} onChange={(v) => updateField("shape", v)} options={["circle", "rect"]} testid="prop-shape" />
                )}
                {"fillColor" in selected && <Field label="fillColor" value={selected.fillColor || ""} onChange={(v) => updateField("fillColor", v)} testid="prop-fillColor" />}
                {"type" in selected && (
                  <Field label="type" type="select" value={selected.type || "omni"} onChange={(v) => updateField("type", v)} options={["omni", "directional", "spot", "ambient"]} testid="prop-type" />
                )}
              </div>
            ) : (
              <div className="card-soft p-4 text-xs text-ink2 italic">Pick a node to edit its properties.</div>
            )}
          </div>

          {/* Right: Compile */}
          <div className="space-y-3">
            <span className="label-eyebrow">Output</span>
            <button
              onClick={compile}
              disabled={busy || nodes.length === 0}
              className="btn-primary text-sm py-2 w-full disabled:opacity-40"
              data-testid="scene-compile"
            >
              {busy ? <><RefreshCw className="w-4 h-4 animate-spin" /> Compiling…</> : <><Sparkles className="w-4 h-4" /> Compile to Swift</>}
            </button>
            {code && (
              <>
                <div className="flex gap-2">
                  <button onClick={copyCode} className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border hover:border-ink transition flex-1 justify-center" data-testid="scene-copy">
                    {copied ? <Check className="w-3 h-3 text-teal" /> : <Copy className="w-3 h-3" />} {copied ? "Copied" : "Copy"}
                  </button>
                  {onApply && (
                    <button onClick={apply} className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink text-cream hover:opacity-90 transition flex-1 justify-center" data-testid="scene-apply">
                      Apply →
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {code && (
          <div className="px-6 pb-6">
            <CodeBlock code={code} filename={filename} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, hint, type = "text", options, step, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-ink2 mb-1 font-bold">{label}</span>
      {type === "select" ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-2 py-1 rounded border border-border bg-paper outline-none focus:border-marigold transition" data-testid={testid}>
          {(options || []).map((o) => <option key={o} value={o}>{o || "(none)"}</option>)}
        </select>
      ) : (
        <input
          type={type}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-2 py-1 rounded border border-border bg-paper outline-none focus:border-marigold transition font-mono text-xs"
          data-testid={testid}
        />
      )}
      {hint && <span className="block text-[10px] text-ink2 mt-0.5">{hint}</span>}
    </label>
  );
}
